import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import DialpadIcon from '@mui/icons-material/Dialpad';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  ApiError,
  PosHit,
  PosLookupKind,
  PosTransaction,
  PosTxnLine,
  RefundTenderWire,
  posCreateRefund,
  posCreateSale,
  posReprintReceipt,
  posLookup,
  posSettings,
  SaleLineRequest,
  SaleTender,
} from '../api';
import { useAuth } from '../auth/AuthContext';
import { useWhoAmI } from '../auth/PosGate';
import CartPanel from './CartPanel';
import CustomItemPanel from './CustomItemPanel';
import RefundPanel from './RefundPanel';
import RefundConfirmDialog from './dialogs/RefundConfirmDialog';
import SearchPanel from './SearchPanel';
import SettingsPanel from './SettingsPanel';
import TransactionsPanel from './TransactionsPanel';
import CashDialog from './dialogs/CashDialog';
import ClearCartDialog from './dialogs/ClearCartDialog';
import EmployeeConfirmDialog from './dialogs/EmployeeConfirmDialog';
import OwnerConfirmDialog from './dialogs/OwnerConfirmDialog';
import ReceiptDialog from './dialogs/ReceiptDialog';
import { stationId } from './station';
import SaleCompleteDialog, { CompletedSale } from './dialogs/SaleCompleteDialog';
import TenderDialog, { TenderKind } from './dialogs/TenderDialog';
import {
  CartLine,
  PriceBasis,
  RefundSelection,
  computeTotals,
  customHit,
  isCustom,
  lineKey,
  retailTotals,
} from './money';
import { COLORS } from './theme';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

/** Barcode scanners emit keystrokes far faster than fingers do. Below
 *  this median gap we treat a burst as a scan and skip the debounce. */
const SCAN_MAX_GAP_MS = 30;
/** Don't call anything a scan until we've seen enough keystrokes to
 *  measure — a fast two-letter typo shouldn't trip it. */
const SCAN_MIN_KEYS = 6;

/** Where we are in the checkout flow. One state at a time keeps the
 *  dialogs mutually exclusive without a pile of booleans. */
type Flow =
  | { kind: 'shopping' }
  | { kind: 'clear-confirm' }
  | { kind: 'employee-confirm' }
  | { kind: 'owner-confirm' }
  | { kind: 'tender' }
  | { kind: 'cash' }
  | { kind: 'receipt'; tender: TenderKind; tenderedCents?: number; changeCents?: number };

/** Which surface owns the left panel. "Custom Amount" is the industry
 *  (and Square) term for ringing up something off-catalog;
 *  "Transactions" is read-only browsing of past sales (R1). */
type EntryMode = 'scan' | 'custom' | 'transactions' | 'settings';

/** Fallback if `/api/v1/pos/settings` can't be reached. Matches the
 *  server default, so a settings blip narrows the browse range rather
 *  than opening it up. */
const FALLBACK_WINDOW_DAYS = 90;

/** How this register's own sales are identified in the transactions
 *  table. Mirrors `POS_VENDOR` / `POS_LOCATION_BRIDGEWATER` in
 *  `backroom_model::locations` — a sale rung up here is always this
 *  pair, so the summary screen can name itself without a lookup. Past
 *  sales carry their own, which may be Square's. */
const POS_VENDOR = 'agspos';
const POS_LOCATION = 'AGSPOS-BW';

/** Seed a refund selection from a sale line. Starts at one unit (or the
 *  whole remainder when less than one is left, e.g. weighed goods). */
/** A fresh idempotency key for the next sale.
 *
 *  `crypto.randomUUID` is unavailable on insecure origins and in older
 *  browsers; the fallback is not a good UUID but it only has to be
 *  unique enough to keep two sales apart, and the server treats it as
 *  an opaque key. */
function newSaleId(): string {
  const c = window.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const rand = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${rand()}${rand()}-${rand()}-4${rand().slice(1)}-a${rand().slice(1)}-${rand()}${rand()}${rand()}`;
}

/** Cart lines in the shape the sale endpoint takes.
 *
 *  Catalog lines deliberately carry no price: the server prices them
 *  from the catalog. Only a custom amount sends money, because there is
 *  no catalog entry to price it from. */
function saleLinesFor(lines: CartLine[]): SaleLineRequest[] {
  return lines.map((l) =>
    isCustom(l.hit)
      ? {
          name: l.hit.name,
          quantity: l.qty,
          unit_price_cents: l.hit.price ?? 0,
        }
      : {
          tenant: l.hit.tenant,
          item_id: l.hit.id,
          quantity: l.qty,
        },
  );
}

function selectionFor(line: PosTxnLine): RefundSelection {
  return {
    lineIndex: line.line_index,
    name: line.name ?? '(unnamed)',
    unitPrice: line.unit_price,
    quantity: Math.min(1, line.refundable_quantity),
    maxQuantity: line.refundable_quantity,
    taxExempt: line.tax_exempt,
    restock: !!line.item_id,
    restockable: !!line.item_id,
  };
}

const RegisterPage: React.FC = () => {
  const { idToken, signOut } = useAuth();
  const me = useWhoAmI();

  const [mode, setMode] = useState<EntryMode>('scan');
  const [windowDays, setWindowDays] = useState(FALLBACK_WINDOW_DAYS);
  // Server-driven: this till has no way to turn test mode on or off,
  // which is the point — see `[pos] test_mode` in the server config.
  const [testMode, setTestMode] = useState(false);
  // Held for the whole sale, not per attempt: it is the idempotency key,
  // so a retry after a lost response has to reuse it.
  const [saleId, setSaleId] = useState<string>(() => newSaleId());
  const [recording, setRecording] = useState(false);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<PosLookupKind>('none');
  const [hits, setHits] = useState<PosHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [lines, setLines] = useState<CartLine[]>([]);
  const [basis, setBasis] = useState<PriceBasis>('retail');
  // Owner withdrawals are god-only. The server enforces it too — this
  // just keeps a dangerous switch off a cashier's screen.
  const canWithdraw = me.roles.includes('god');
  const [flow, setFlow] = useState<Flow>({ kind: 'shopping' });
  const [completed, setCompleted] = useState<CompletedSale | null>(null);
  const [undo, setUndo] = useState<CartLine | null>(null);

  // --- Refund cart (R2) -----------------------------------------------
  // Selections always belong to exactly one sale. Toggling a line from a
  // different sale replaces the cart rather than mixing two sales into
  // one refund, which the ledger couldn't represent anyway.
  const [refundTxn, setRefundTxn] = useState<{
    vendor: string;
    location: string;
    id: string;
    /** How the original sale was priced. An owner withdrawal took no
     *  money, so returning one is a restock and the preview must show
     *  zero rather than the cost it was recorded at. */
    basis: PriceBasis;
  } | null>(null);
  const [refundLines, setRefundLines] = useState<RefundSelection[]>([]);
  const [refundReason, setRefundReason] = useState('');
  const [refundNote, setRefundNote] = useState('');
  // Widened to the sale vocabulary: a withdrawal reversal is tendered
  // `none`, which the refund picker never offers and the server accepts
  // only for that case.
  const [refundTender, setRefundTender] = useState<RefundTenderWire>('cash');
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundConfirm, setRefundConfirm] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [toast, setToast] = useState('');
  /** Bumped after a successful refund so the day reloads and the
   *  remaining-quantity figures reflect what just happened. */
  const [refundTick, setRefundTick] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Monotonic request id — a slow response from an earlier query must
   *  never overwrite results from a later one. */
  const seqRef = useRef(0);
  /** Keystroke timestamps for scan detection; cleared on every commit. */
  const keyTimesRef = useRef<number[]>([]);

  // One shot at mount. Failure is silent and falls back to the server
  // default — the browse clamp is convenience; the server enforces the
  // real one on every request.
  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    posSettings(idToken)
      .then((s) => {
            if (!cancelled) {
          setWindowDays(s.refund_window_days);
          setTestMode(s.test_mode);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  const totals = useMemo(() => computeTotals(lines, basis), [lines, basis]);
  const retail = useMemo(() => retailTotals(lines), [lines]);
  const dialogOpen =
    flow.kind !== 'shopping' || completed !== null || refundConfirm;

  const clearRefund = useCallback(() => {
    setRefundTxn(null);
    setRefundLines([]);
    setRefundReason('');
    setRefundNote('');
    setRefundTender('cash');
    setRefundError('');
  }, []);

  /** Add or remove one line from the refund cart. */
  const toggleRefundLine = useCallback(
    (txn: PosTransaction, line: PosTxnLine) => {
      setRefundError('');
      const sameSale =
        refundTxn?.id === txn.id &&
        refundTxn?.vendor === txn.vendor &&
        refundTxn?.location === txn.location;

      if (!sameSale) {
        // Switching sales resets the cart — a refund is always against
        // one transaction.
        const reversal = txn.price_basis === 'owner';
        setRefundTxn({
          vendor: txn.vendor,
          location: txn.location,
          id: txn.id,
          basis: reversal ? 'owner' : 'retail',
        });
        // No cash moves on a withdrawal reversal, and the server only
        // accepts `none` for exactly this case — so it isn't the
        // cashier's choice to get wrong.
        setRefundTender(reversal ? 'none' : 'cash');
        setRefundReason('');
        setRefundNote('');
        setRefundLines(
          line.refundable_quantity > 0 ? [selectionFor(line)] : [],
        );
        return;
      }

      setRefundLines((prev) => {
        const existing = prev.find((s) => s.lineIndex === line.line_index);
        if (existing) return prev.filter((s) => s.lineIndex !== line.line_index);
        if (line.refundable_quantity <= 0) return prev;
        return [...prev, selectionFor(line)].sort(
          (a, b) => a.lineIndex - b.lineIndex,
        );
      });
    },
    [refundTxn],
  );

  const changeRefundQty = useCallback((lineIndex: number, delta: number) => {
    setRefundLines((prev) =>
      prev.flatMap((s) => {
        if (s.lineIndex !== lineIndex) return [s];
        const next = s.quantity + delta;
        if (next <= 0) return [];
        return [{ ...s, quantity: Math.min(next, s.maxQuantity) }];
      }),
    );
  }, []);

  const submitRefund = useCallback(async () => {
    if (!idToken || !refundTxn || refundLines.length === 0) return;
    setRefundBusy(true);
    setRefundError('');
    try {
      const res = await posCreateRefund(idToken, {
        vendor: refundTxn.vendor,
        location: refundTxn.location,
        transaction_id: refundTxn.id,
        tender: refundTender,
        reason: refundReason,
        note: refundNote.trim() === '' ? undefined : refundNote.trim(),
        // Quantities only — the server prices the refund from the
        // stored sale.
        lines: refundLines.map((s) => ({
          line_index: s.lineIndex,
          quantity: s.quantity,
          restock: s.restock,
        })),
        print_receipt: true,
        station_id: stationId(),
      });
      setRefundConfirm(false);
      clearRefund();
      setRefundTick((n) => n + 1);
      const amount = (res.total_cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'CAD',
      });
      // Say when the slip didn't print. A refund with no paper is worth
      // knowing about at the counter, not just in a log.
      setToast(
        res.receipt_printed
          ? `Refund recorded — ${amount}`
          : `Refund recorded — ${amount}. No receipt printed.`,
      );
    } catch (e) {
      setRefundConfirm(false);
      if (e instanceof ApiError && e.status === 409) {
        // Someone refunded these lines first. Reload so the cashier
        // sees the real remaining quantities rather than stale ones.
        setRefundError(
          e.body || 'Those lines have already been refunded. Reloading.',
        );
        setRefundTick((n) => n + 1);
        setRefundLines([]);
      } else if (e instanceof ApiError) {
        setRefundError(e.body || `Refund failed — API error ${e.status}`);
      } else {
        setRefundError('Refund failed — check network');
      }
    } finally {
      setRefundBusy(false);
    }
  }, [
    idToken,
    refundTxn,
    refundLines,
    refundTender,
    refundReason,
    refundNote,
    clearRefund,
  ]);

  // -------------------------------------------------------------------
  // Cart
  // -------------------------------------------------------------------

  const addHit = useCallback((hit: PosHit) => {
    if (hit.price == null) return;
    const key = lineKey(hit);
    setLines((prev) => {
      const existing = prev.find((l) => lineKey(l.hit) === key);
      if (existing) {
        // Same item twice = quantity 2, not two lines. Also refresh the
        // stored hit so a re-scan picks up a price change.
        return prev.map((l) =>
          lineKey(l.hit) === key ? { hit, qty: l.qty + 1 } : l,
        );
      }
      return [...prev, { hit, qty: 1 }];
    });
    setQuery('');
    setHits([]);
    setKind('none');
    keyTimesRef.current = [];
  }, []);

  /** Ring up an off-catalog line. Synthesised as a `PosHit` so totals,
   *  quantity, and employee pricing need no special case. */
  const addCustom = useCallback((name: string, priceCents: number) => {
    setLines((prev) => [...prev, { hit: customHit(name, priceCents), qty: 1 }]);
  }, []);

  const changeQty = useCallback((key: string, delta: number) => {
    setLines((prev) =>
      prev.flatMap((l) => {
        if (lineKey(l.hit) !== key) return [l];
        const qty = l.qty + delta;
        return qty <= 0 ? [] : [{ ...l, qty }];
      }),
    );
  }, []);

  // Removal is the one destructive action without a confirm — it gets
  // an undo instead, which is faster and less annoying at a counter.
  const removeLine = useCallback(
    (key: string) => {
      const line = lines.find((l) => lineKey(l.hit) === key);
      if (line) setUndo(line);
      setLines((prev) => prev.filter((l) => lineKey(l.hit) !== key));
    },
    [lines],
  );

  const clearCart = useCallback(() => {
    setLines([]);
    setBasis('retail');
    setQuery('');
    setHits([]);
    setKind('none');
    // Back to scanning for the next customer — leaving the till parked
    // on the keypad would swallow the first scan of the next sale.
    setMode('scan');
  }, []);

  // -------------------------------------------------------------------
  // Lookup
  // -------------------------------------------------------------------

  /** Run a lookup now. `autoAdd` is set on the scanner path: an
   *  unambiguous code match rings itself up so a scan is never two
   *  actions. */
  const runLookup = useCallback(
    async (text: string, autoAdd: boolean) => {
      if (!idToken) return;
      const seq = ++seqRef.current;
      setLoading(true);
      setError('');
      try {
        const res = await posLookup(idToken, text);
        if (seq !== seqRef.current) return;
        if (
          autoAdd &&
          (res.kind === 'upc' || res.kind === 'sku') &&
          res.hits.length === 1 &&
          res.hits[0].price != null
        ) {
          addHit(res.hits[0]);
          return;
        }
        setKind(res.kind);
        setHits(res.hits);
      } catch (e) {
        if (seq !== seqRef.current) return;
        setKind('none');
        setHits([]);
        setError(
          e instanceof ApiError
            ? `Catalog offline — API error ${e.status}`
            : 'Catalog offline — check network',
        );
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    },
    [idToken, addHit],
  );

  // Debounced lookup as the cashier types.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LEN) {
      // Bump the sequence so an in-flight response for a longer query
      // can't repopulate the list we just cleared.
      seqRef.current += 1;
      setHits([]);
      setKind('none');
      setLoading(false);
      setError('');
      return;
    }
    debounceRef.current = setTimeout(() => runLookup(trimmed, false), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runLookup]);

  /** Median inter-keystroke gap under the scanner threshold. */
  const isLikelyScan = () => {
    const times = keyTimesRef.current;
    if (times.length < SCAN_MIN_KEYS) return false;
    const gaps: number[] = [];
    for (let i = 1; i < times.length; i += 1) gaps.push(times[i] - times[i - 1]);
    gaps.sort((a, b) => a - b);
    return gaps[Math.floor(gaps.length / 2)] < SCAN_MAX_GAP_MS;
  };

  const handleQueryChange = (next: string) => {
    keyTimesRef.current = next === '' ? [] : [...keyTimesRef.current, Date.now()];
    setQuery(next);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      keyTimesRef.current = [];
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed === '') return;
    // A scanner terminates its burst with Enter. Either way, Enter means
    // "go now" — skip the debounce.
    const scan = isLikelyScan();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    void runLookup(trimmed, true);
    if (!scan) keyTimesRef.current = [];
  };

  // -------------------------------------------------------------------
  // Focus trap — the scanner is a keyboard, so if focus drifts off the
  // search field, scans go nowhere. Any stray keystroke pulls it back.
  // -------------------------------------------------------------------
  useEffect(() => {
    // Only in scan mode: there's no search field in custom mode, and
    // stealing focus there would fight the name box and the keypad.
    if (dialogOpen || mode !== 'scan') return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typingElsewhere =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
      if (typingElsewhere) return;
      // Ignore pure modifiers so Ctrl/Alt combos still reach the browser.
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialogOpen, mode]);

  useEffect(() => {
    if (!dialogOpen && mode === 'scan') inputRef.current?.focus();
  }, [dialogOpen, mode]);

  // -------------------------------------------------------------------
  // Checkout flow
  // -------------------------------------------------------------------

  const startPurchase = useCallback(() => {
    if (lines.length === 0) return;
    // An owner withdrawal skips tender entirely: there is nothing to
    // pay, so asking "cash or card?" would be a lie with a keypad
    // attached.
    if (basis === 'owner') {
      setFlow({ kind: 'owner-confirm' });
      return;
    }
    setFlow(basis === 'employee' ? { kind: 'employee-confirm' } : { kind: 'tender' });
  }, [lines.length, basis]);

  // Shortcuts. F2/F4 are chosen because they're unlikely to collide with
  // anything a scanner emits.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (dialogOpen) return;
      if (e.key === 'F2') {
        e.preventDefault();
        // Toggles employee only. Owner is a deliberate click, never a
        // keystroke away from an ordinary sale.
        setBasis((b) => (b === 'employee' ? 'retail' : 'employee'));
      } else if (e.key === 'F4') {
        e.preventDefault();
        startPurchase();
      } else if (e.key === 'Delete' && e.ctrlKey) {
        e.preventDefault();
        if (lines.length > 0) setFlow({ kind: 'clear-confirm' });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialogOpen, startPurchase, lines.length]);

  /** Record the sale, then show the summary.
   *
   *  Deliberately the last step, after the receipt prompt: the card is
   *  charged on the terminal before the cashier gets here, so a decline
   *  means we never wrote anything.
   *
   *  A failed write does NOT hide the summary. The money has already
   *  changed hands, so the cashier needs the change due and the totals
   *  regardless; what they get instead is a loud warning that the sale
   *  didn't record, and the same id retries safely. */
  const finishSale = async (
    tender: SaleTender,
    wantsReceipt: boolean,
    tenderedCents?: number,
    changeCents?: number,
  ) => {
    if (!idToken || recording) return;
    setRecording(true);
    setFlow({ kind: 'shopping' });

    let recorded = false;
    let receiptPrinted = false;
    try {
      const res = await posCreateSale(idToken, {
        id: saleId,
        tender,
        price_basis: basis,
        lines: saleLinesFor(lines),
        print_receipt: wantsReceipt,
        station_id: stationId(),
        // Cash figures ride along for the paper only — the server's
        // record of the sale is its total, however it was paid.
        tendered_cents: tenderedCents,
        change_cents: changeCents,
      });
      recorded = true;
      receiptPrinted = res.receipt_printed;
      // Only rotate the key once the sale is safely down. A failed
      // attempt keeps its id so a retry dedupes instead of double-
      // recording.
      setSaleId(newSaleId());
    } catch (e) {
      const msg = e instanceof ApiError ? `API error ${e.status}` : (e as Error)?.message;
      setToast(`SALE NOT RECORDED — ${msg ?? 'unknown error'}. Tell a manager.`);
    } finally {
      setRecording(false);
    }

    setCompleted({
      // The id the sale was recorded under — not the rotated one.
      id: recorded ? saleId : undefined,
      total: totals.total,
      tender,
      tenderedCents,
      changeCents,
      wantsReceipt,
      receiptPrinted,
      employee: basis === 'employee',
      owner: basis === 'owner',
      recorded,
      isTest: testMode,
    });
  };

  /** Print a receipt for a sale that is already recorded — from the
   *  summary screen, or from the Transactions list days later.
   *
   *  Sends only the sale's identity: the server rebuilds the receipt
   *  from the ledger and marks it REPRINT. */
  const reprintReceipt = useCallback(
    async (vendor: string, location: string, transactionId: string) => {
      if (!idToken) return;
      try {
        const res = await posReprintReceipt(idToken, {
          vendor,
          location,
          transaction_id: transactionId,
          station_id: stationId(),
        });
        setToast(
          res.receipt_printed
            ? 'Receipt sent to the printer.'
            : 'No receipt printed — check the printer in Settings.',
        );
      } catch (e) {
        setToast(
          e instanceof ApiError
            ? `Reprint failed — ${e.body || `API error ${e.status}`}`
            : 'Reprint failed — check the network.',
        );
      }
    },
    [idToken],
  );

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        // Same treatment employee pricing gets: the chrome changes, so
        // nobody has to remember which server this till is pointed at.
        ...(testMode
          ? { border: `4px solid ${COLORS.warn}`, boxSizing: 'border-box' }
          : {}),
      }}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: COLORS.surface,
          borderBottom: `1px solid ${COLORS.border}`,
          flexShrink: 0,
        }}
      >
        <PointOfSaleIcon sx={{ color: COLORS.accent }} />
        <Typography sx={{ fontWeight: 800, letterSpacing: '0.04em' }}>
          AGS POS
        </Typography>
        {testMode && (
          <Box
            sx={{
              backgroundColor: COLORS.warn,
              color: '#000',
              fontWeight: 800,
              letterSpacing: '0.08em',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              fontSize: '0.8rem',
            }}
          >
            TEST MODE — SALES ARE NOT REAL
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
          Bridgewater · {me.name || me.email}
        </Typography>
        <Button size="small" onClick={signOut} sx={{ color: COLORS.textMuted }}>
          Sign out
        </Button>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={(_e, next: EntryMode | null) => {
                // Exclusive groups emit null when the active button is
                // re-pressed; ignore it so the panel can't blank out.
                if (next === null) return;
                // §9.6.6: the right rail can't be a sale cart and a
                // refund cart at once, and silently swapping it is how
                // the wrong thing gets refunded.
                if (next === 'transactions' && lines.length > 0) {
                  setToast(
                    'Finish or clear the current sale before opening Transactions.',
                  );
                  return;
                }
                if (mode === 'transactions' && next !== 'transactions') {
                  clearRefund();
                }
                setMode(next);
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  borderRadius: 999,
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.textMuted,
                },
                '& .Mui-selected': {
                  backgroundColor: `${COLORS.accent} !important`,
                  color: '#ffffff !important',
                },
                gap: 1,
              }}
            >
              <ToggleButton value="scan">
                <QrCodeScannerIcon fontSize="small" sx={{ mr: 1 }} />
                Scan
              </ToggleButton>
              <ToggleButton value="custom">
                <DialpadIcon fontSize="small" sx={{ mr: 1 }} />
                Custom
              </ToggleButton>
              <ToggleButton value="transactions">
                <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} />
                Transactions
              </ToggleButton>
              <ToggleButton value="settings">
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                Settings
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {mode === 'scan' ? (
            <SearchPanel
              query={query}
              onQueryChange={handleQueryChange}
              onKeyDown={handleSearchKeyDown}
              inputRef={inputRef}
              kind={kind}
              hits={hits}
              loading={loading}
              error={error}
              onPick={addHit}
            />
          ) : mode === 'custom' ? (
            <CustomItemPanel onAdd={addCustom} suspended={dialogOpen} />
          ) : mode === 'settings' ? (
            <SettingsPanel
              idToken={idToken ?? ''}
              testMode={testMode}
              refundWindowDays={windowDays}
              onToast={setToast}
            />
          ) : (
            <TransactionsPanel
              onPrintReceipt={(txn) =>
                void reprintReceipt(txn.vendor, txn.location, txn.id)
              }
              refundWindowDays={windowDays}
              reloadTick={refundTick}
              refundTxnId={refundTxn?.id ?? null}
              refundLineIndices={refundLines.map((s) => s.lineIndex)}
              refundError={refundError}
              onToggleLine={toggleRefundLine}
            />
          )}
        </Box>
        {mode === 'transactions' ? (
          <RefundPanel
            selections={refundLines}
            basis={refundTxn?.basis ?? 'retail'}
            reason={refundReason}
            note={refundNote}
            tender={refundTender}
            busy={refundBusy}
            onQtyChange={changeRefundQty}
            onRestockChange={(idx, restock) =>
              setRefundLines((prev) =>
                prev.map((s) => (s.lineIndex === idx ? { ...s, restock } : s)),
              )
            }
            onRemove={(idx) =>
              setRefundLines((prev) => prev.filter((s) => s.lineIndex !== idx))
            }
            onReasonChange={setRefundReason}
            onNoteChange={setRefundNote}
            onTenderChange={setRefundTender}
            onClear={clearRefund}
            onRefund={() => setRefundConfirm(true)}
          />
        ) : (
          <CartPanel
            lines={lines}
            basis={basis}
            onBasisChange={setBasis}
            canWithdraw={canWithdraw}
            onQtyChange={changeQty}
            onRemove={removeLine}
            onClear={() => setFlow({ kind: 'clear-confirm' })}
            onPurchase={startPurchase}
          />
        )}
      </Box>

      <ClearCartDialog
        open={flow.kind === 'clear-confirm'}
        lineCount={lines.length}
        total={totals.total}
        onCancel={() => setFlow({ kind: 'shopping' })}
        onConfirm={() => {
          clearCart();
          setFlow({ kind: 'shopping' });
        }}
      />

      <OwnerConfirmDialog
        open={flow.kind === 'owner-confirm'}
        lines={lines}
        onCancel={() => setFlow({ kind: 'shopping' })}
        onConfirm={() => {
          // No tender, no change, no receipt prompt — record it and
          // show the summary.
          void finishSale('none', false);
        }}
      />

      <EmployeeConfirmDialog
        open={flow.kind === 'employee-confirm'}
        lines={lines}
        employeeTotals={totals}
        retailTotals={retail}
        onCancel={() => setFlow({ kind: 'shopping' })}
        onConfirm={() => setFlow({ kind: 'tender' })}
      />

      <TenderDialog
        open={flow.kind === 'tender'}
        total={totals.total}
        onCancel={() => setFlow({ kind: 'shopping' })}
        onPick={(t) =>
          setFlow(t === 'cash' ? { kind: 'cash' } : { kind: 'receipt', tender: 'card' })
        }
      />

      <CashDialog
        open={flow.kind === 'cash'}
        total={totals.total}
        onCancel={() => setFlow({ kind: 'tender' })}
        onComplete={(tenderedCents) =>
          setFlow({
            kind: 'receipt',
            tender: 'cash',
            tenderedCents,
            changeCents: tenderedCents - totals.total,
          })
        }
      />

      <ReceiptDialog
        open={flow.kind === 'receipt'}
        onAnswer={(wants) => {
          if (flow.kind !== 'receipt') return;
          void finishSale(flow.tender, wants, flow.tenderedCents, flow.changeCents);
        }}
      />

      <SaleCompleteDialog
        sale={completed}
        onPrintReceipt={() => {
          if (!completed?.id) return;
          void reprintReceipt(POS_VENDOR, POS_LOCATION, completed.id);
        }}
        onDismiss={() => {
          setCompleted(null);
          clearCart();
        }}
      />

      <RefundConfirmDialog
        open={refundConfirm}
        selections={refundLines}
        basis={refundTxn?.basis ?? 'retail'}
        reason={refundReason}
        tender={refundTender}
        busy={refundBusy}
        onCancel={() => setRefundConfirm(false)}
        onConfirm={submitRefund}
      />

      <Snackbar
        open={toast !== ''}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast('')}>
          {toast}
        </Alert>
      </Snackbar>

      <Snackbar
        open={undo !== null}
        autoHideDuration={5000}
        onClose={() => setUndo(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                if (undo) setLines((prev) => [...prev, undo]);
                setUndo(null);
              }}
            >
              UNDO
            </Button>
          }
        >
          Removed {undo?.hit.name}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegisterPage;
